import { z } from 'zod';

import { WIZARD_MAX_CREATIVES } from './campaign-wizard.js';
import { listQuerySchema } from './common.js';
import type { MessageRole } from './enums.js';

/**
 * AGENT — Claude tool-calling katmanı (§4.3).
 *
 * ---------------------------------------------------------------------------
 * Ekranın tasarım ekseni: ajan ASLA kendi başına para hareket ettirmez
 * ---------------------------------------------------------------------------
 * Yazma araçları HİÇBİR ŞEY UYGULAMAZ; bir ÖNERİ üretir ve sohbete onay kartı
 * olarak düşer. Onaylanınca aksiyon otomasyonla AYNI yoldan geçer — aynı
 * guardrail, aynı idempotency anahtarı, aynı execution log. Autopilot yetkisi
 * yalnızca Workflow modülünde.
 *
 * ---------------------------------------------------------------------------
 * Araçlar İNCE bir katman
 * ---------------------------------------------------------------------------
 * Her araç mevcut servis katmanını çağırır ve iş mantığını TEKRARLAMAZ.
 * `get_metrics` `AnalyticsQueryService`i çağırır; kendi SQL'ini yazan bir araç
 * bir gün ekrandan farklı sayı döndürür ve kullanıcı hangisine inanacağını
 * bilemez.
 *
 * ---------------------------------------------------------------------------
 * 🔴 LLM sağlayıcısı yapılandırılmamış
 * ---------------------------------------------------------------------------
 * `ANTHROPIC_API_KEY` .env'de YOK. `LLMProvider` soyutlaması, araç kayıt
 * defteri, konuşma kalıcılığı ve uçlar HAZIR olur; `NotConfiguredLLMProvider`
 * tipli `LLM_NOT_CONFIGURED` (HTTP 503) döner. Arayüz bunu görünce sohbet
 * kutusunu kilitler ve nedenini yazar.
 */

// ═══════════════════════════════════════════════════════════════════════════
// LLM sağlayıcı soyutlaması
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `LLMProvider.stream(input): AsyncIterable<LLMStreamEvent>`
 *
 * Sağlayıcı bağımsız olay akışı. Anthropic'in kendi olay adlarını dışarı
 * sızdırmıyoruz ki sağlayıcı değişince arayüz değişmesin.
 */
export type LLMStreamEvent =
  | { kind: 'text_delta'; text: string }
  | { kind: 'tool_use'; toolCallId: string; name: string; input: unknown }
  | { kind: 'tool_result'; toolCallId: string; ok: boolean; result: unknown; error?: string }
  | { kind: 'usage'; inputTokens: number; outputTokens: number }
  | { kind: 'stop'; reason: 'end_turn' | 'max_tokens' | 'tool_use' | 'canceled' | 'error' }
  | { kind: 'error'; code: string; message: string };

/// Varsayılan model. `AGENT_MODEL` env anahtarı ile ezilebilir.
export const DEFAULT_AGENT_MODEL = 'claude-sonnet-4-5';

// ═══════════════════════════════════════════════════════════════════════════
// Araç kayıt defteri
// ═══════════════════════════════════════════════════════════════════════════

export const AGENT_TOOLS = [
  'get_metrics',
  'get_campaigns',
  'analyze_campaigns',
  'get_connections',
  'get_credit_balance',
  'list_workflows',
  'create_workflow',
  'adjust_budget',
  'pause_campaign',
  'generate_creative',
  // ── Genişleme Adım 3 ────────────────────────────────────────────────────
  // Katalog sağlığı okuma + metin düzeltme önerisi.
  //
  // İkisi AYRI araç ve bu zorunlu: düzeltme aracı ürünün MEVCUT sorunlarını
  // bilmeden çağrılırsa model neyi neden düzelttiğini söyleyemez ve
  // kullanıcı onay kutusunda gerekçesiz bir metin görür.
  'get_catalog_health',
  // `write` — HİÇBİR ŞEY UYGULAMIYOR, öneri üretiyor. Mağazaya yazma yalnız
  // kullanıcı onayından sonra.
  'fix_product_content',
  // ── Tek AI yüzeyi, Faz 1: katalog gözü ──────────────────────────────────
  //
  // Üçü de OKUMA. Amaç, ajanın "hangi ürün", "hangi şablon", "hangi manken"
  // sorularını KENDİ cevaplayabilmesi: bu bilgiler bugüne kadar yalnız kreatif
  // tezgâhının ve kampanya direktörünün sistem prompt'una gömülüydü, yani ajan
  // sohbetinde erişilemiyordu.
  //
  // 🔴 Sıra SONA ekleniyor ve öyle kalmalı. Araç tanımları prompt önekinin
  // başında render ediliyor ve o önek artık ÖNBELLEKLENİYOR
  // (`anthropic.llm.provider.ts` → `cache_control`); araya bir araç sokmak
  // önbelleği her istekte boşa çıkarır.
  'list_products',
  'get_product',
  'list_creative_catalog',
  // ── Faz 2: sıra sıra ilerleme ───────────────────────────────────────────
  //
  // Kullanıcının isteği: *"takıldığı her yerde soru sorup ilerletebilecek."*
  // Bu araç o cümlenin karşılığı ve kampanya kipine değil BÜTÜN SOHBETE ait.
  'ask_user',
  /**
   * Kütüphane + SÜREN İŞLER, tek araçta.
   *
   * Kullanıcı "görsellerim" derken hazır olanı da üretilmekte olanı da
   * kastediyor; ikisini iki araca bölmek her "hazır mı?" sorusunda modele
   * yanlış aracı seçme şansı verirdi.
   */
  'list_creatives',
  // ── Faz 4: kampanya ─────────────────────────────────────────────────────
  //
  // TEK araç, TEK onay kartı, ÜÇ platform çağrısı. Üç ayrı yazma aracı
  // yazılmadı: kullanıcı ikisini onaylayıp üçüncüyü reddettiğinde platformda
  // reklamsız bir ad set kalırdı ve o yarım nesnenin neden orada olduğu
  // hiçbir ekranda yazmazdı.
  'create_campaign',
] as const;
export type AgentToolName = (typeof AGENT_TOOLS)[number];

export interface AgentToolDefinition {
  name: AgentToolName;
  /**
   * `read` araçlar doğrudan çalışır ve sonucu döner.
   * `write` araçlar HİÇBİR ŞEY UYGULAMAZ — `AgentProposal` üretir.
   * `ask` araçlar TURU DURDURUR: kullanıcıya bir soru bırakıp beklerler.
   *
   * Üçüncü tür ayrı bir kavram çünkü döngüdeki davranışı ötekilerin ikisine de
   * benzemiyor: sonuç dönüyor ama model onunla devam ETMİYOR — cevap bir
   * sonraki turda, kullanıcıdan geliyor.
   */
  kind: 'read' | 'write' | 'ask';
  /// Aracı çağırabilmek için gereken en düşük workspace rolü.
  minRole: 'VIEWER' | 'MEMBER' | 'ADMIN' | 'OWNER';
  /// Hangi servisi çağırıyor — denetlenebilirlik için sözleşmede yazılı.
  backedBy:
    | 'analytics'
    | 'integrations'
    | 'automation'
    | 'creative'
    | 'billing'
    | 'commerce'
    | 'agent'
    | 'rmf';
  descriptionKey: string;
  /// Arayüz kapatabilsin diye; bugün hepsi açık (kalıcı bayrak deposu yok).
  enabled: boolean;
}

export const AGENT_TOOL_REGISTRY: Record<AgentToolName, AgentToolDefinition> = {
  get_metrics: {
    name: 'get_metrics',
    kind: 'read',
    minRole: 'VIEWER',
    backedBy: 'analytics',
    descriptionKey: 'agent.tools.get_metrics',
    enabled: true,
  },
  get_campaigns: {
    name: 'get_campaigns',
    kind: 'read',
    minRole: 'VIEWER',
    backedBy: 'analytics',
    descriptionKey: 'agent.tools.get_campaigns',
    enabled: true,
  },
  analyze_campaigns: {
    name: 'analyze_campaigns',
    kind: 'read',
    minRole: 'VIEWER',
    backedBy: 'analytics',
    descriptionKey: 'agent.tools.analyze_campaigns',
    enabled: true,
  },
  get_connections: {
    name: 'get_connections',
    kind: 'read',
    minRole: 'VIEWER',
    backedBy: 'integrations',
    descriptionKey: 'agent.tools.get_connections',
    enabled: true,
  },
  get_credit_balance: {
    name: 'get_credit_balance',
    kind: 'read',
    minRole: 'VIEWER',
    backedBy: 'billing',
    descriptionKey: 'agent.tools.get_credit_balance',
    enabled: true,
  },
  list_workflows: {
    name: 'list_workflows',
    kind: 'read',
    minRole: 'VIEWER',
    backedBy: 'automation',
    descriptionKey: 'agent.tools.list_workflows',
    enabled: true,
  },
  create_workflow: {
    name: 'create_workflow',
    kind: 'write',
    minRole: 'ADMIN',
    backedBy: 'automation',
    descriptionKey: 'agent.tools.create_workflow',
    enabled: true,
  },
  adjust_budget: {
    name: 'adjust_budget',
    kind: 'write',
    minRole: 'MEMBER',
    backedBy: 'automation',
    descriptionKey: 'agent.tools.adjust_budget',
    enabled: true,
  },
  pause_campaign: {
    name: 'pause_campaign',
    kind: 'write',
    minRole: 'MEMBER',
    backedBy: 'automation',
    descriptionKey: 'agent.tools.pause_campaign',
    enabled: true,
  },
  generate_creative: {
    name: 'generate_creative',
    kind: 'write',
    minRole: 'MEMBER',
    backedBy: 'creative',
    descriptionKey: 'agent.tools.generate_creative',
    enabled: true,
  },
  get_catalog_health: {
    name: 'get_catalog_health',
    kind: 'read',
    minRole: 'VIEWER',
    backedBy: 'commerce',
    descriptionKey: 'agent.tools.get_catalog_health',
    enabled: true,
  },
  fix_product_content: {
    name: 'fix_product_content',
    kind: 'write',
    minRole: 'MEMBER',
    backedBy: 'commerce',
    descriptionKey: 'agent.tools.fix_product_content',
    enabled: true,
  },

  /**
   * 🔴 `minRole` ELLE SEÇİLMİYOR — yeteneğin eşiğiyle aynı olmak ZORUNDA.
   *
   * `list_products` ve `get_product` `report.read` (VIEWER),
   * `list_creative_catalog` `creative.read` (VIEWER) altında yaşıyor
   * (`CAPABILITY_MIN_ROLE`). Buraya daha yüksek bir rol yazmak, ekrandan
   * okuyabildiği bir şeyi ajana soramayan bir kullanıcı üretirdi — ve o farkı
   * hiçbir ekran açıklayamazdı.
   */
  list_products: {
    name: 'list_products',
    kind: 'read',
    minRole: 'VIEWER',
    backedBy: 'commerce',
    descriptionKey: 'agent.tools.list_products',
    enabled: true,
  },
  get_product: {
    name: 'get_product',
    kind: 'read',
    minRole: 'VIEWER',
    backedBy: 'commerce',
    descriptionKey: 'agent.tools.get_product',
    enabled: true,
  },
  list_creative_catalog: {
    name: 'list_creative_catalog',
    kind: 'read',
    minRole: 'VIEWER',
    backedBy: 'creative',
    descriptionKey: 'agent.tools.list_creative_catalog',
    enabled: true,
  },
  /**
   * SORU SORMA — hiçbir servisi çağırmıyor, `backedBy: 'agent'`.
   *
   * `minRole: 'VIEWER'`: soru sormak bir yetki gerektirmez. Yazma yetkisi
   * olmayan bir izleyicinin de ajana danışabilmesi gerekiyor.
   */
  list_creatives: {
    name: 'list_creatives',
    kind: 'read',
    minRole: 'VIEWER',
    backedBy: 'creative',
    descriptionKey: 'agent.tools.list_creatives',
    enabled: true,
  },
  /**
   * `backedBy: 'rmf'` — kampanya/ad set/reklam oluşturan tek yol.
   *
   * `minRole: 'MEMBER'`, çünkü `campaign.write` yeteneğinin eşiği o
   * (`CAPABILITY_MIN_ROLE`). Daha yükseğe çekmek, ekrandaki sihirbazdan
   * kampanya açabilen ama ajandan açamayan bir rol üretirdi.
   */
  create_campaign: {
    name: 'create_campaign',
    kind: 'write',
    minRole: 'MEMBER',
    backedBy: 'rmf',
    descriptionKey: 'agent.tools.create_campaign',
    enabled: true,
  },
  ask_user: {
    name: 'ask_user',
    kind: 'ask',
    minRole: 'VIEWER',
    backedBy: 'agent',
    descriptionKey: 'agent.tools.ask_user',
    enabled: true,
  },
};

/**
 * 🔴 Araç girdisindeki `workspaceId` İSTEMCİDEN DEĞİL OTURUMDAN gelir.
 *
 * Modelin ürettiği araç girdisi kullanıcı metninden etkilenebilir. Kapsam
 * parametresini modele bırakmak, "başka bir workspace'in verisini getir"
 * yazan bir kullanıcının bunu başarması demektir. Araç çağrıları workspace
 * guard'ından geçer ve kapsam SUNUCUDA enjekte edilir.
 */
export const TOOL_SCOPE_COMES_FROM_SESSION = true;

// ═══════════════════════════════════════════════════════════════════════════
// Öneri (onay kartı)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * 🔴 Önerinin ŞEMADA EVİ YOK.
 *
 * `WorkflowActionLog` → `WorkflowRun` → `Workflow` zinciri zorunlu; ajanın
 * ürettiği tek seferlik aksiyonun bağlanacağı bir `Workflow` satırı yok.
 *
 * Sözleşme kararı (a): workspace başına GİZLİ BİR SİSTEM WORKFLOW'U açılır —
 * `name = '__agent__'`, `enabled = false`, `scope.system = 'agent'`. Ajan
 * önerileri onun altında bir `WorkflowRun` + `WorkflowActionLog` olarak
 * yaşar. Şema değişikliği gerekmez ve onaylanan aksiyon otomasyonla AYNI
 * guardrail/idempotency/log yolundan geçer — ki §4.3'ün istediği tam bu.
 *
 * Bedeli: kural listesi ekranı bu satırı FİLTRELEMEK ZORUNDA
 * (`scope.system === 'agent'` olanlar gizlenir). Filtrelenmezse kullanıcı
 * kurmadığı bir kural görür.
 */
export const AGENT_SYSTEM_WORKFLOW_NAME = '__agent__';

/**
 * Kreatif onaylarının gizli sistem kuralı — Şema borç turu.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden AJANINKİ PAYLAŞILMADI
 * ═══════════════════════════════════════════════════════════════════════════
 * `AgentProposalService.listPending` bekleyen önerileri KURAL ADIYLA buluyor
 * (`workflow: { name: AGENT_SYSTEM_WORKFLOW_NAME }`). Kreatif onayları aynı
 * kuralın altına yazılsaydı ajanın öneri listesine sızarlardı ve orada
 * `inputMetrics.agent` taşımadıkları için `tool`u boş kartlar olarak
 * görünürlerdi. Ayrı ad, o sızıntıyı yapısal olarak imkânsız kılıyor —
 * ajanın hiçbir sorgusu değişmedi.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden BU DOSYADA — `moderation.ts`te değil
 * ═══════════════════════════════════════════════════════════════════════════
 * Aşağıdaki liste ikisini birlikte tutuyor ve KURAL LİSTESİ EKRANLARININ
 * TEK FİLTRESİ o. İki adı iki dosyaya bölmek, listeye eklemeyi unutmayı
 * mümkün kılardı — ve o unutmanın bedeli kullanıcının kurmadığı bir kuralı
 * ekranda görmesi. Adların yan yana durması, aralarındaki bağın tek görünen
 * hâli.
 */
export const CREATIVE_SYSTEM_WORKFLOW_NAME = '__creative__';

/**
 * Kullanıcı arayüzünde GİZLENECEK kural adları.
 *
 * 🔴 Yeni bir sistem kuralı açan her adım bu listeye girer. Girmezse kural
 * listesi, koşu listesi, onboarding sayacı ve ajan bağlamı kullanıcının
 * kurmadığı bir kuralı gösterir — dört ayrı ekranda, dördü de aynı sebeple.
 * `system-workflow.spec.ts` listenin bütün üyelerinin filtrelendiğini
 * doğruluyor.
 */
export const SYSTEM_WORKFLOW_NAMES: readonly string[] = [
  AGENT_SYSTEM_WORKFLOW_NAME,
  CREATIVE_SYSTEM_WORKFLOW_NAME,
];

export interface AgentProposal {
  /// = `WorkflowActionLog.id`
  proposalId: string;
  tool: AgentToolName;
  /// Okunur özet: i18n anahtarı + parametre.
  summaryKey: string;
  params: Record<string, string | number>;
  /// 🔴 `PRODUCT` bir metrik seviyesi DEĞİL, aksiyonun HEDEFİ (Adım 3).
  /// `WorkflowActionLog.targetLevel` `MetricLevel` tipinde ve katalog
  /// düzeltmesinin hedefi bir ürün. Ayrı bir onay yolu açmak yasaktı, o
  /// yüzden hedef tipi genişledi — gerekçe `enums.ts`te.
  targetLevel: 'CAMPAIGN' | 'ADSET' | 'AD' | 'PRODUCT' | null;
  targetId: string | null;
  targetName: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  /// Guardrail ön kontrolü — öneri kartı "bu değişiklik tavanı aşıyor"
  /// diyebilsin; kullanıcı onaylayıp sonra reddedilmesin.
  guardrailCheck: {
    passed: boolean;
    /// Aşılan kural (`budget_percent`, `daily_limit`, `entitlement`).
    violations: string[];
  };
  expiresAt: string;
  /**
   * `superseded`: kullanıcı düzeltme yapınca ajan AYNI sohbette yeni bir öneri
   * üretti ve bu kart onun yerine geçti.
   *
   * `expired`den AYRI olmak zorunda: süresi dolan bir kart "geç kaldınız"
   * derken, yerine yenisi gelen kart "bu artık yanlış" diyor. İkisi tek kod
   * taşısaydı kullanıcı eskimiş bir kampanyayı süresi dolmuş sanıp yenisini
   * beklerdi — oysa yenisi zaten ekranda.
   */
  status: 'pending' | 'approved' | 'rejected' | 'expired' | 'superseded' | 'applied' | 'failed';
  /**
   * Kartın DOĞDUĞU sohbet.
   *
   * ═══════════════════════════════════════════════════════════════════════
   * Neden eklendi
   * ═══════════════════════════════════════════════════════════════════════
   * Kimlik `inputMetrics.agent.conversationId` içinde BAŞTAN BERİ duruyordu
   * ama dışarı verilmiyordu. Sonucu ölçüldü: `GET /agent/proposals` kartın
   * hangi sohbette üretildiğini söylemediği için bağlam paneli aynı kartın
   * İKİNCİ bir kopyasını çiziyordu — bir bileşen, iki yer.
   *
   * Alan gelince panel "şu sohbete git" satırına indi: kart tek yerde,
   * doğduğu konuşmanın içinde yaşıyor.
   */
  conversationId: string | null;
}

export const proposalDecisionSchema = z.object({
  decision: z.enum(['approve', 'reject']),
  note: z.string().trim().max(300).optional(),
  /**
   * Marka kimliği (Meta'da Sayfa kimliği) — yalnız `create_campaign` kartında.
   *
   * ═══════════════════════════════════════════════════════════════════════
   * 🔴 KARTA GELİYOR, PLANA DEĞİL — ve bu bilinçli
   * ═══════════════════════════════════════════════════════════════════════
   * Plan araç çağrısında doğuyor; modelin bu numarayı bilmesi MÜMKÜN DEĞİL
   * ve uydurması reklamın yanlış marka adına yayınlanması demek. Onaya basan
   * İNSAN biliyor — elle sihirbaz da aynı numarayı aynı yerden alıyor
   * (`identityRefStorageKey`, tarayıcıda hesap başına), yani kullanıcı onu
   * ikinci kez öğrenmiyor.
   *
   * Neden gerekli: ajan HER ZAMAN yeni bir ad set açıyor ve Meta yeni bir ad
   * set'in İLK reklamında sayfayı kardeş bir reklamdan devralamıyor
   * (`meta.connector.ts` → `resolvePageId` net bir hata fırlatıyor). Boş
   * gönderilirse zincir, kampanya ve ad set PLATFORMDA OLUŞTUKTAN SONRA
   * düşer ve geri alınamaz.
   *
   * Opsiyonel çünkü `reject` kararında ve kampanya dışı kartlarda anlamı yok;
   * zorunluluk uygulama katmanında, kampanya dalında.
   */
  identityRef: z.string().trim().min(1).max(128).optional(),
});
export type ProposalDecisionInput = z.infer<typeof proposalDecisionSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Konuşma ve mesajlar
// ═══════════════════════════════════════════════════════════════════════════

export interface ConversationRow {
  id: string;
  title: string | null;
  createdAt: string;
  updatedAt: string;
  messageCount: number;
  /// Son mesajın ilk satırı — yan listede önizleme.
  preview: string | null;
  /**
   * Sohbeti HANGİ MOTOR sürüyor (22 Ağu 2026).
   *
   * 🔴 Liste satırının taşıması ZORUNLU: kullanıcı bir kampanya oturumuna
   * tıkladığında konsol onu GENEL ajan sohbeti olarak açarsa kart, şıklar ve
   * form kaybolur — kaydedilmiş bir konuşma açılıp bozuk görünür. Kip yalnız
   * sunucuda bilinseydi konsol her tıklamada bir istek daha atmak zorunda
   * kalırdı.
   */
  kind: 'GENERAL' | 'CAMPAIGN';
}

export const conversationListQuerySchema = listQuerySchema.extend({
  sort: z.enum(['updatedAt', 'createdAt']).default('updatedAt'),
});

export const conversationCreateSchema = z.object({
  title: z.string().trim().max(120).optional(),
  /// İlk mesaj — boş sohbet açmadan doğrudan soru sorulabilsin.
  message: z.string().trim().max(8000).optional(),
});
export type ConversationCreateInput = z.infer<typeof conversationCreateSchema>;

export const conversationRenameSchema = z.object({
  title: z.string().trim().min(1).max(120),
});

export interface AgentMessageRow {
  id: string;
  role: MessageRole;
  /// Anthropic content block dizisi olarak saklanıyor. Arayüz metin
  /// bloklarını basar, `tool_use` bloklarını AJ3 adımı olarak açar.
  content: unknown[];
  toolCalls:
    | {
        toolCallId: string;
        name: string;
        input: unknown;
        /// 🔴 `GET /agent/conversations/:id` yanıtında KIRPILIYOR — ~25 KB'lık
        /// ham gövde ekranda hiç basılmıyor. Gerekçe `AgentCard` başlığında.
        result?: unknown;
        ok?: boolean;
        /// Ekranın çizdiği dar zarf. Bu KIRPILMIYOR: kartın kalıcılığı buna bağlı.
        ui?: AgentCard | null;
      }[]
    | null;
  inputTokens: number | null;
  outputTokens: number | null;
  model: string | null;
  createdAt: string;
  /// Bu mesajda üretilmiş öneriler (onay kartları).
  proposals: AgentProposal[];
}

/**
 * AJ1 / AJ11 — SSE ile streaming.
 *
 * İptal: istemci bağlantıyı kapatınca üretim durdurulur ve O ANA KADARKİ
 * metin mesaj olarak KAYDEDİLİR. Yarım yanıtı çöpe atmak, kullanıcının
 * gördüğü şeyi geçmişte bulamaması demek.
 */
/**
 * Mesaja iliştirilen görseller — AJ13.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden yalnız GÖRSEL, neden bu üç mime
 * ═══════════════════════════════════════════════════════════════════════════
 * Sağlayıcı `image` bloğu olarak JPEG / PNG / GIF / WebP kabul ediyor; video
 * hiç kabul etmiyor. Yükleme ucunun beyaz listesi (`UPLOAD_MIME_ALLOWLIST`)
 * mp4'ü de içeriyor çünkü orada asıl iş kreatif üretimi — burada aynı listeyi
 * kullanmak, ajana gönderilemeyecek bir dosyayı yüklettirmek olurdu.
 *
 * GIF dışarıda: yalnız ilk kare okunuyor ve kullanıcı hareketli bir görsel
 * gönderip "bunu izle" diyemez; sessizce ilk kareyi analiz etmek yanlış cevaba
 * yol açar.
 *
 * ---------------------------------------------------------------------------
 * Sınırlar NEREDEN geliyor
 * ---------------------------------------------------------------------------
 * Sağlayıcı sınırı görsel başına 10 MB (base64 hâliyle) ve istek gövdesi
 * toplamda 32 MB. Base64 ham baytı ~%33 şişiriyor, yani 5 MB'lık bir dosya
 * ~6,7 MB olarak gidiyor: dört tanesi 27 MB, gövde sınırının altında.
 * Bu yüzden tavan 5 MB ve mesaj başına 4 görsel — ikisi birlikte seçildi,
 * biri değiştirilecekse diğeri de hesaplanmalı.
 */
export const AGENT_ATTACHMENT_MIME = ['image/jpeg', 'image/png', 'image/webp'] as const;
export const AGENT_ATTACHMENT_LIMITS = {
  maxPerMessage: 4,
  /// Ham dosya baytı; base64 sonrası ~1,33 katı.
  maxBytes: 5 * 1024 * 1024,
} as const;

export const messageAttachmentSchema = z.object({
  /// `POST /creatives/uploads` biletinden dönen S3 anahtarı. Sunucu bunun BU
  /// çalışma alanına ait olduğunu doğrular — istemciden geldiği için
  /// güvenilmez (`isOwnObjectKey`).
  assetKey: z.string().min(1).max(400),
  mimeType: z.enum(AGENT_ATTACHMENT_MIME),
  /// Yalnız arayüzde gösterilir; sağlayıcıya gitmiyor.
  fileName: z.string().trim().max(200).optional(),
});
export type MessageAttachmentInput = z.infer<typeof messageAttachmentSchema>;

/* ══════════════════════════════════════════════════════════════════════════
   EKRAN KARTLARI — araç sonucunun DOKUNULABİLİR yüzü
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * Bir aracın ekranda çizilen hâli.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden ayrı bir `card` SSE olayı DEĞİL
 * ═══════════════════════════════════════════════════════════════════════════
 * Kart bir aracın SONUCU. Ayrı bir kare olsaydı hangi adımın altına oturacağı
 * sözleşmeden okunamazdı, iptal edilen turda sahipsiz kalırdı ve `toolCalls`
 * içinde bir evi olmazdı — yani sayfa yenilenince ölürdü. `tool_result` zaten
 * `toolCallId` taşıyor; kart o kimliğin altına oturuyor.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 `ui` ≠ `result` — ve bu ayrım bir ÖLÇÜMÜN sonucu
 * ═══════════════════════════════════════════════════════════════════════════
 * `agent-turns.ts` araç sonucunu bilerek ATIYOR: tek bir `get_metrics` +
 * `get_campaigns` turu RSC yüküne ~25 KB JSON ekliyordu ve ekranda karşılığı
 * yoktu. Kural gevşemiyor, KESKİNLEŞİYOR:
 *
 *   · `result` = modelin gördüğü ham gövde. Satırda saklanıyor, `GET
 *     /agent/conversations/:id` yanıtında KIRPILIYOR.
 *   · `ui`     = ekranın gördüğü dar zarf. Saklanıyor VE dönüyor.
 *
 * Cevaplanmış kart ayrıca çöküyor: seçenek listesi düşüyor, yalnız seçilen
 * satır kalıyor. Kırpma "sonucu at" değil, "KARARI TUT, seçenekleri at" — ve
 * kullanıcının sohbetten okumak isteyeceği şey tam olarak karardır.
 */
export type AgentCard =
  /**
   * Şıklı soru.
   *
   * `text` BURADA da duruyor ama arayüz onu BASMIYOR: soruyu model zaten
   * cümlesinin içinde yazıyor ve iki kez basmak 22 Ağu 2026'da bir kez
   * düzeltilmiş bir hataydı. Alan yine de taşınıyor — cevaplanmış kartın
   * neyin cevabı olduğu geçmişte okunabilmeli.
   */
  | { kind: 'ask'; text: string; options: string[]; answered: string | null }
  /**
   * ÜRÜN SEÇİCİ — `list_products` sonucunun dokunulabilir hâli.
   *
   * ═══════════════════════════════════════════════════════════════════════
   * Neden ayrı bir "seç" aracı YOK
   * ═══════════════════════════════════════════════════════════════════════
   * Ürün listesinin doğru ekran biçimi zaten bir ızgaradır — modelin onu
   * cümle içinde saymasının hiçbir üstünlüğü yok. Kart listenin KENDİSİ;
   * dokunmak da o listeye verilen cevap. Ayrı bir `pick_product` aracı,
   * modele "şimdi seçtireyim mi" diye ikinci bir karar verdirirdi ve o
   * kararı yanlış verdiği her turda kullanıcı düz metin okurdu.
   *
   * 🔴 `imageUrl` KARTA YAZILABİLİR: mağaza CDN'inin açık adresi, imzalı
   * değil. Kreatif önizlemeleri öyle DEĞİL — gerekçe `creative` dalında.
   */
  | {
      kind: 'product';
      products: {
        productId: string;
        title: string;
        priceRange: string | null;
        imageUrl: string | null;
        platform: string;
        /// Reklamı engelleyen durum: görselsiz ya da adressiz ürün seçilemez.
        blocker: 'noImage' | 'noUrl' | null;
      }[];
      /// Kaç satır gizlendi — kullanıcı listenin tamamı sanmasın.
      total: number;
      picked: { productId: string; title: string; imageUrl: string | null } | null;
    }
  /**
   * KREATİF SEÇİCİ — ÇOKLU seçim.
   *
   * Her kreatif BİR REKLAM olacağı için seçim tekil değil: `create_campaign`
   * `creativeIds` istiyor ve kullanıcı "şu üçüyle açalım" diyebilmeli.
   *
   * 🔴 ÖNİZLEME ADRESİ TAŞINMIYOR. `CreativeRow.thumbUrl` kısa ömürlü bir
   * imzalı adres; kalıcı bir karta yazılsaydı iki saat sonra sessizce kırık
   * bir görsel olurdu. Kart kimlik taşıyor, arayüz adresleri çizim anında
   * `GET /creatives?ids=` ile tazeliyor.
   */
  | {
      kind: 'creative';
      creatives: {
        creativeId: string;
        type: string;
        name: string | null;
        width: number | null;
        height: number | null;
      }[];
      picked: string[];
    };

/**
 * Karta verilen cevap.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * İKİ DAL ve aradaki fark ÖLÇÜLMÜŞ bir hatadan geliyor
 * ═══════════════════════════════════════════════════════════════════════════
 * `ask` dalında `value` DÜZ METİN ve kullanıcı balonu olarak basılıyor —
 * şıkkın metni cevabın kendisi. Değer taşıyan bir şık, konuşma geçmişinde
 * koduyla görünür ve model bir sonraki turda okuyamaz.
 *
 * Seçici dallarında (ürün/hesap/kreatif — Faz 3/4) `value` bir KİMLİK olacak
 * ve balon basılmayacak: model kırpılmış bir liste görüyor, arayüz tamamını
 * gösteriyor ve kullanıcı modelin göremediği bir satırı seçebiliyor. O alan
 * arayüzün, modelin değil.
 */
export const cardReplySchema = z.discriminatedUnion('kind', [
  z.object({
    toolCallId: z.string().min(1).max(120),
    kind: z.literal('ask'),
    /// Şıkkın METNİ — cevabın kendisi.
    value: z.string().trim().min(1).max(400),
  }),
  z.object({
    toolCallId: z.string().min(1).max(120),
    kind: z.literal('product'),
    /// Ürün KİMLİĞİ. Balon basılmıyor; kart "seçildi" hâline geçiyor.
    value: z.uuid(),
  }),
  z.object({
    toolCallId: z.string().min(1).max(120),
    kind: z.literal('creative'),
    /**
     * ÇOKLU: her kreatif bir reklam.
     *
     * Tavan `WIZARD_MAX_CREATIVES`ten OKUNUYOR, elle yazılmıyor. İkisi bir
     * dönem tesadüfen 10'du: kartın seçicisi o sabiti kullanıyor, bu şema ise
     * kendi 10'unu taşıyordu. Biri değiştiğinde kullanıcı seçebildiği bir
     * kreatif kümesiyle 400 alırdı.
     */
    values: z.array(z.uuid()).min(1).max(WIZARD_MAX_CREATIVES),
  }),
]);
export type CardReplyInput = z.infer<typeof cardReplySchema>;

/**
 * SEÇİMİN MESAJDA saklanan hâli.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden bir metin bloğu DEĞİL
 * ═══════════════════════════════════════════════════════════════════════════
 * Seçim modele ULAŞMAK ZORUNDA — yoksa model kullanıcının ne seçtiğini
 * bilmiyor ve bir sonraki turda yeniden soruyor. Ama ekranda BALON olarak
 * görünmemeli: kartın kendisi "seçildi" hâline geçiyor ve altına bir de
 * kimlik yazan bir baloncuk koymak aynı bilgiyi iki kez göstermek olurdu.
 *
 * `mentorivo_intent` ile birebir aynı desen: blok satırda duruyor, arayüz
 * onu atlıyor, `replayContent` modele giderken tek satırlık metne çeviriyor.
 */
export const AGENT_PICK_BLOCK_TYPE = 'mentorivo_pick';

/**
 * Tur başına EN FAZLA BİR SORU.
 *
 * İki soru sormak 22 Ağu 2026'da somut bir arızaya yol açtı: kullanıcı aynı
 * anda iki farklı "sonraki adım" görüyor ve hangisine cevap vereceğini
 * bilemiyordu. Sınır sözleşmede, ihlal sunucuda kırpılıyor.
 */
export const AGENT_ASK_LIMITS = { maxPerTurn: 1, maxOptions: 4 } as const;

/**
 * NİYET — modül seçicinin yerini alan şey (tek AI yüzeyi, Faz 1).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Seçim bir KİP DEĞİL, bir İPUCU
 * ═══════════════════════════════════════════════════════════════════════════
 * Eski `AgentToolMode` konsolun mesaj sütununu ayrı bir motora devrediyordu:
 * seçmek ekran değiştirmekti ve seçmeden aynı iş yapılamıyordu. Kullanıcının
 * söylediği şey netti — *"sadece AI'ye haber verecek, farklı bir yerde konuşma
 * başlatmayacak"*.
 *
 * Niyet o yüzden mesajın METNİNE değil YANINA yazılıyor ve tek yaptığı şey
 * modele bir cümle iletmek. Motor değişmiyor, uç değişmiyor, sohbet açılmıyor.
 *
 * 🔴 HİÇBİR ZAMAN ZORUNLU DEĞİL. Kullanıcı düz metinle "kampanya oluştur"
 * yazdığında model aynı araç ailesini çağırıyor — araçlar araç defterinde ve
 * seçime bağlı değil. Niyet yalnız belirsizliği azaltıyor.
 *
 * Metne önek gömmek (`/kampanya …`) REDDEDİLDİ: API tek dilli, yani önek bir
 * dilde yanlış olurdu; ve `agent-composer.tsx` → `mention()` aynı kararı zaten
 * vermiş: *"gizli bir kimlik eklemek, kullanıcının okuduğu cümleyle gönderilen
 * cümleyi ayırırdı."*
 */
export const AGENT_INTENTS = ['campaign', 'creative', 'catalog', 'automation'] as const;
export type AgentIntent = (typeof AGENT_INTENTS)[number];

/**
 * Niyetin MESAJDA saklanan hâli.
 *
 * `mentorivo_image` ve `mentorivo_meta` ile aynı desen: Anthropic content-block
 * dizisinde yaşayan ama sağlayıcıya HAM GİTMEYEN bir blok. `replayContent` onu
 * modele giderken tek satırlık bir metne çeviriyor; arayüz ham hâlini okuyup
 * kullanıcı balonunun üstüne sessiz bir etiket basıyor.
 *
 * Sistem prompt'una YAZILMIYOR ve bu bir performans kararı: prompt öneki artık
 * `cache_control` ile önbellekli (`anthropic.llm.provider.ts`) ve her mesajda
 * değişen bir satır o önbelleği her istekte çürütürdü. Niyet zaten tek bir
 * mesaja ait; evi de o mesaj.
 */
export const AGENT_INTENT_BLOCK_TYPE = 'mentorivo_intent';

export const messageSendSchema = z
  .object({
    /**
     * 🔴 `min(1)` YOK — görsel tek başına gönderilebilmeli.
     *
     * "Şu ekran görüntüsüne bak" demek için kullanıcıyı ayrıca bir şey yazmaya
     * zorlamak yapay bir engel olurdu. Boşluk kontrolü aşağıdaki `refine`da:
     * metin de ek de yoksa gönderilecek bir şey yok.
     */
    message: z.string().trim().max(8000),
    /// Arayüz aynı mesajı iki kez göndermesin diye; `Idempotency-Key` ile aynı
    /// işi yapıyor ama sohbete özel ve zorunlu.
    clientMessageId: z.uuid(),
    attachments: z
      .array(messageAttachmentSchema)
      .max(AGENT_ATTACHMENT_LIMITS.maxPerMessage)
      .optional(),
    /// Yetenek şeridinden ya da composer çipinden gelen niyet. Tek TURLUK.
    intent: z.enum(AGENT_INTENTS).optional(),
    /**
     * Bir KARTA verilen cevap — düz mesajın yerine geçiyor.
     *
     * Sunucu bunu, bekleyen `tool_result`u TAMAMLAYAN bir tur olarak işliyor:
     * model kendi sorduğu soruya cevabı aynı araç çağrısının sonucu olarak
     * görüyor ve konuşma kaldığı yerden sürüyor. Ayrı bir uç açmak, aynı
     * döngüyü ikinci kez yazmak olurdu.
     */
    cardReply: cardReplySchema.optional(),
  })
  .refine(
    (value) =>
      value.message.length > 0 ||
      (value.attachments?.length ?? 0) > 0 ||
      value.cardReply !== undefined,
    { message: 'Bir şey yazın ya da görsel ekleyin', path: ['message'] },
  );
export type MessageSendInput = z.infer<typeof messageSendSchema>;

/// SSE olay adları — arayüz bunlara abone olur.
export const AGENT_SSE_EVENT = {
  DELTA: 'delta',
  TOOL_USE: 'tool_use',
  TOOL_RESULT: 'tool_result',
  PROPOSAL: 'proposal',
  USAGE: 'usage',
  DONE: 'done',
  ERROR: 'error',
} as const;

// ═══════════════════════════════════════════════════════════════════════════
// Bağlam, öneriler, limit
// ═══════════════════════════════════════════════════════════════════════════

/**
 * AJ12 — ajanın ne gördüğü. Aynı yapı sistem prompt'una da girer.
 *
 * JETON, SIR ve e-posta adresi GİRMEZ. Modelin çıktısı kullanıcıya gidiyor;
 * prompt'a koyduğunuz her şey bir gün ekranda görünebilir.
 */
export interface AgentContextResponse {
  workspace: { name: string; timezone: string; currency: string; today: string };
  /**
   * Reklam hesapları.
   *
   * 🔴 `id` sistem prompt'una da giriyor ve bu bir gereklilik: kampanya kuran
   * araç `adAccountId` istiyor ve o kimlik BİZİM `AdAccount.id`imiz, platformun
   * değil. Prompt'ta olmasaydı model her kampanya turunda önce
   * `get_connections` çağırmak zorunda kalırdı — bir araç çağrısı, bir gecikme
   * ve tur başına sekizlik bütçeden bir kalem.
   */
  connections: { id: string; platform: string; name: string; currency: string; health: string }[];
  /**
   * Bağlı MAĞAZALAR — reklam hesaplarından ayrı (Faz 1).
   *
   * Ürün kataloğunun kaynağı bunlar ve `list_products` bir `platform` süzgeci
   * taşıyor. Model hangi platformların bağlı olduğunu bilmeden o süzgece
   * bağlı olmayan bir değer yazabilir ve boş bir liste alıp "kataloğunuz boş"
   * diye yanlış cevap verebilirdi.
   *
   * Kimlik TAŞIMIYOR: hiçbir araç mağaza bağlantısı kimliği istemiyor ve
   * prompt'a giren her alanın bir tüketicisi olmalı.
   */
  stores: { platform: string; name: string; currency: string }[];
  activeWorkflows: { id: string; name: string; mode: string }[];
  plan: { tier: string; adSpendLimit: number; maxAdAccounts: number; maxMembers: number };
  credits: { available: number; reserved: number };
  /// AJ10 — kalan hak.
  dailyMessages: { used: number; limit: number; resetsAt: string };
  /// Sağlayıcı hazır mı; değilse arayüz sohbeti kilitler.
  llmConfigured: boolean;
  model: string;
  /// Hangi araçlar bu kullanıcı için açık (rol + bayrak).
  availableTools: AgentToolName[];
}

/// AJ8 — boş sohbette gösterilecek örnek sorular. i18n anahtarı + parametre
/// döner, DÜZ METİN DEĞİL: iki dilli üründe sabit metin bir dilde yanlış olur.
export interface AgentSuggestion {
  key: string;
  params?: Record<string, string | number>;
  /// Hangi duruma özel: hesap yoksa "önce hesap bağla".
  condition: 'no_connection' | 'no_data' | 'has_data' | 'has_workflows';
}

// ═══════════════════════════════════════════════════════════════════════════
// Dürüstlük ve kötüye kullanım
// ═══════════════════════════════════════════════════════════════════════════

/**
 * AJ9 — DÜRÜSTLÜK KURALI.
 *
 * Tool hata verirse hata kullanıcıya AYNEN iletilir; tool sonucu olmadan
 * metrik ÜRETİLMEZ. Sistem prompt'unda yazılı olması yetmez — model onu ihlal
 * edebilir ve ihlal ettiğinde kimse fark etmez.
 *
 * Sunucu tarafı işaret: yanıt akışında hiç `tool_result` YOKKEN sayısal iddia
 * içeren bir yanıt üretilirse mesaj `unverifiedNumbers: true` ile
 * işaretlenir ve arayüz uyarı rozeti basar. Test listesine "API kapalıyken
 * ajana metrik sor" adımı girer.
 */
export const AGENT_LIMITS = {
  /// Tek mesajın en fazla karakteri.
  maxMessageChars: 8000,
  /// Bir konuşmadaki en fazla mesaj; aşılınca yeni sohbet önerilir.
  maxMessagesPerConversation: 200,
  /// Bağlam penceresine sığdırmak için son N mesaj tutulur, öncesi özetlenir.
  contextWindowMessages: 30,
  /// Dakika başına mesaj (throttle).
  messagesPerMinute: 10,
  /// Bir yanıtta en fazla araç çağrısı — sonsuz döngüye karşı.
  maxToolCallsPerTurn: 8,
} as const;

export const AgentErrorCode = {
  /// `ANTHROPIC_API_KEY` yok. HTTP 503.
  LLM_NOT_CONFIGURED: 'LLM_NOT_CONFIGURED',
  CONVERSATION_NOT_FOUND: 'CONVERSATION_NOT_FOUND',
  /// Plan bazlı günlük mesaj limiti doldu. `details: {used, limit, resetsAt}`.
  DAILY_MESSAGE_LIMIT: 'DAILY_MESSAGE_LIMIT',
  /// Konuşma mesaj sayısı sınırına ulaştı.
  CONVERSATION_TOO_LONG: 'CONVERSATION_TOO_LONG',
  /// Araç bu rol için kapalı.
  TOOL_FORBIDDEN: 'TOOL_FORBIDDEN',
  TOOL_DISABLED: 'TOOL_DISABLED',
  PROPOSAL_NOT_FOUND: 'PROPOSAL_NOT_FOUND',
  PROPOSAL_EXPIRED: 'PROPOSAL_EXPIRED',
  PROPOSAL_ALREADY_DECIDED: 'PROPOSAL_ALREADY_DECIDED',
  /// Grace bitti; yazma araçları kapalı, okuma açık.
  WRITE_TOOLS_DISABLED: 'WRITE_TOOLS_DISABLED',
  /// Sağlayıcı hata verdi ve yeniden denemeler onu kurtaramadı. Ham metin
  /// kullanıcıya GİTMİYOR (17 Ağu 2026 kararı); ayrıntı yalnız sunucu
  /// kaydında. Kalıcı sınıf: 400 geçersiz istek, 401 anahtar, ağ kopması.
  PROVIDER_ERROR: 'PROVIDER_ERROR',
  /**
   * Sağlayıcı GEÇİCİ olarak kapasite dışı (`overloaded_error`, 429, 5xx) ve
   * geri çekilmeli yeniden denemeler de tükendi.
   *
   * `PROVIDER_ERROR`'den AYRI olmasının sebebi ekranda okunan cümle: burada
   * kullanıcının yapması gereken tek şey BEKLEMEK. Aynı kodu taşısaydı
   * "servis kullanılamıyor" derdi ve kullanıcı bir yapılandırma arızası
   * sanıp desteğe yazardı — oysa bir dakika sonra çalışacak.
   */
  PROVIDER_BUSY: 'PROVIDER_BUSY',

  // ── Faz 4 — ajan kimliği ────────────────────────────────────────────────
  AGENT_NOT_FOUND: 'AGENT_NOT_FOUND',
  /// `@@unique([workspaceId, name])` ihlali. HTTP 409.
  AGENT_NAME_TAKEN: 'AGENT_NAME_TAKEN',
  /// `__` ön ekli ad — kullanıcı kendi ajanını kendi ekranından gizleyemesin.
  AGENT_NAME_RESERVED: 'AGENT_NAME_RESERVED',
  /// Bağlanmak istenen kural bu workspace'te yok ya da sistem kuralı.
  AGENT_WORKFLOW_NOT_ASSIGNABLE: 'AGENT_WORKFLOW_NOT_ASSIGNABLE',

  // ── AJ13 — mesaj eki ────────────────────────────────────────────────────
  /// Ek görselin S3 anahtarı BU çalışma alanına ait değil. HTTP 403.
  /// Sessizce atılmıyor: atılsaydı kullanıcı görselini gönderdiğini sanıp
  /// ajanın onu neden görmediğini anlamazdı.
  ATTACHMENT_NOT_OWNED: 'ATTACHMENT_NOT_OWNED',
  /// Ek okunamadı (depo kapalı ya da nesne yok). HTTP 503.
  ATTACHMENT_UNREADABLE: 'ATTACHMENT_UNREADABLE',
} as const;
export type AgentErrorCode = (typeof AgentErrorCode)[keyof typeof AgentErrorCode];

/**
 * `/admin/ai` bu turda SALT OKUNUR.
 *
 * Sistem prompt'u, model seçimi, araç bayrakları ve günlük limit ezmesi için
 * kalıcı bir ayar deposu yok (`PlatformSetting` önerisi `semaDegisikligi`'nde)
 * ve `agent_conversations` bugün 0 satır. Yazılabilir bir editör, hiçbir
 * kodun okumadığı ayarları düzenleyen bir ekran olurdu. Ekran şu an geçerli
 * yapılandırmayı GÖSTERİR.
 */
export interface AdminAgentConfig {
  llmConfigured: boolean;
  model: string;
  /// Prompt'un kendisi değil, ilk 500 karakteri — destek "hangi sürüm
  /// çalışıyor" sorusunu cevaplasın, prompt kopyalanabilir olmasın.
  systemPromptPreview: string;
  tools: AgentToolDefinition[];
  dailyLimits: Record<string, number>;
  /// Tüm kiracılarda toplam konuşma/mesaj — bugün 0.
  usage: { conversations: number; messages: number; last7dMessages: number };
}
